import { useCallback } from 'react';
import useQueryParams from './useQueryParams';

enum Entries {
  SEARCH = 'search',
  PAGE = 'page',
  SORT_DIR = 'sortDir',
  SORT_BY = 'sortBy',
  FILTERS = 'filters',
}

export type OrderBy = 'updatedAt' | 'messages';
type OrderDir = 'asc' | 'desc';

export interface ConversationFilter {
  key: string;
  value: string;
}

export interface ConversationParams {
  search: string;
  page: number;
  sortDir: OrderDir;
  sortBy: OrderBy;
  filters: ConversationFilter[];
}

export function useConversationsQueryParams(limit: number) {
  const loadParamsFromURL = useCallback((searchParams: URLSearchParams): ConversationParams => {
    const rawPage = searchParams.get(Entries.PAGE);

    let page = 1;
    if (rawPage) {
      page = parseInt(rawPage, 10);
    }

    const search = searchParams.get(Entries.SEARCH);
    const sortBy = (searchParams.get(Entries.SORT_BY) || 'updatedAt') as OrderBy;
    const sortDir = (searchParams.get(Entries.SORT_DIR) || 'desc') as OrderDir;
    return {
      search: search || '',
      sortBy,
      sortDir,
      page,
      filters: parseFilters(searchParams.get(Entries.FILTERS)),
    };
  }, []);

  const { searchParams, setParams, setEntry } = useQueryParams<Entries>();

  const params = loadParamsFromURL(searchParams);

  const setPage = (p: number) => {
    setEntry(Entries.PAGE, p.toString());
  };

  const setSearch = (s?: string) => {
    const list = [
      {
        key: Entries.PAGE,
        value: '1',
      },
      { key: Entries.SEARCH, value: s || '' },
    ];
    setParams(list);
  };

  const setDataFilters = (filters: ConversationFilter[]) => {
    setParams([
      { key: Entries.PAGE, value: '1' },
      { key: Entries.FILTERS, value: filters.length ? JSON.stringify(filters) : '' },
    ]);
  };

  const setSort = (dir?: string, type?: string) => {
    const list = [];

    if (type) {
      list.push({
        key: Entries.SORT_BY,
        value: type,
      });
    }

    if (dir) {
      list.push({
        key: Entries.SORT_DIR,
        value: dir,
      });
    }
    setParams(list);
  };

  const stringifyURLParams = () => {
    let url = `?limit=${limit}`;

    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null && value !== '') {
        const serializedValue = key === 'filters' ? JSON.stringify(value) : String(value);
        url += `&${encodeURIComponent(key)}=${encodeURIComponent(serializedValue)}`;
      }
    }

    return url;
  };

  return {
    params,
    setParams,
    setPage,
    setSearch,
    setSort,
    setDataFilters,
    stringifyURLParams,
  };
}

function parseFilters(value: string | null): ConversationFilter[] {
  if (!value) return [];

  try {
    const parsed: unknown = JSON.parse(value);
    if (!Array.isArray(parsed)) return [];

    return parsed.filter(
      (filter): filter is ConversationFilter =>
        typeof filter === 'object' &&
        filter !== null &&
        typeof (filter as ConversationFilter).key === 'string' &&
        typeof (filter as ConversationFilter).value === 'string' &&
        Boolean((filter as ConversationFilter).key) &&
        Boolean((filter as ConversationFilter).value),
    );
  } catch {
    return [];
  }
}
